import {ReactNode} from 'react';

import {Localizer} from '../../../../../libs/shared/core/src/helpers/internationalization/localizeTypes';
import {defineMessage} from '../../../../../libs/shared/core/src/helpers/internationalization/message';
import {ModifiersEnum} from '../../../../../srcCommon/inboxes/search/searchModifiersHelpers';
import {
  SearchHintModel,
  SearchHintValue,
  SearchStaticHintTypesEnum,
} from '../../../../models/api/searchHintModel';
import {InboxesSearchLocationsEnum} from '../../../../models/url/models/inboxesUrlModel';
import {InboxesSearchParameters} from '../../../../store/panels/inboxes/parameters/inboxesParameters';
import {
  SearchLocalFilterIconType,
  SearchLocalFilterTypesEnum,
} from '../../../../store/panels/inboxes/parameters/inboxesParametersFiltersIconHelpers';
import {VisualLocationSearchHint, VisualModifierSearchHint, VisualSearchHintValue} from './searchHintsTypes';

export function serializeToModifierVisualHint(
  hint: SearchHintModel,
  searchParameters: InboxesSearchParameters,
  localize: Localizer,
): VisualModifierSearchHint | undefined {
  const {query: searchQuery} = searchParameters;
  const {type, values} = hint;

  const visualHintValues = values.map((hintValue, index) => ({
    // There's no guarantee that `<modifier>:<value>` is unique (e.g. for a tag with the same alias in multiple namespaces).
    // Using index in the key is acceptable here, because a value is not the same if its position changed within `values`.
    key: `${hintValue.modifier}-${hintValue.value}-${index}`,
    value: hintValue.value,
    modifier: hintValue.modifier,
    emoji: hintValue.emoji,
    ...computeTitleAndIconForHintValue(hintValue, localize),
    isSelected: Boolean(
      hintValue.modifier &&
        searchQuery.includes(`${hintValue.modifier}:${maybeWrapInDoubleQuotes(hintValue.value)}`),
    ),
  }));

  return {
    type: hint.type,
    values: visualHintValues,
    isTransient: hint.isTransient,
    key: `${type}-${visualHintValues.map((value) => value.key).join('-')}`,
  };
}

function computeTitleAndIconForHintValue(
  hintValue: SearchHintValue,
  localize: Localizer,
): Pick<VisualSearchHintValue, 'title' | 'icon'> {
  const {modifier, title, value} = hintValue;
  const displayedValue = title || maybeWrapInDoubleQuotes(value);

  const defaultTitleAndIcon: Pick<VisualSearchHintValue, 'title' | 'icon'> = {
    title: title || `${modifier}:${value}`,
    icon: {
      type: SearchLocalFilterTypesEnum.ICON,
      iconName: 'helpFilledSmall',
    },
  };

  switch (modifier) {
    case ModifiersEnum.ACCOUNT:
      return {
        title: localize(
          defineMessage({
            id: 'search-hint-account',
            message: `Account ${displayedValue}`,
          }),
        ),
        icon: {
          type: SearchLocalFilterTypesEnum.ICON,
          iconName: 'briefcaseOutlineSmall',
          selectedIconName: 'briefcaseFilledSmall',
        },
      };

    case ModifiersEnum.AUTHOR:
      return {
        title: localize(
          defineMessage({
            id: 'search-hint-author',
            message: `Author ${displayedValue}`,
          }),
        ),
        icon: {
          type: SearchLocalFilterTypesEnum.ICON,
          iconName: 'participantSmall',
          selectedIconName: 'participantFilledSmall',
        },
      };

    case ModifiersEnum.BODY:
      return {
        title: localize(
          defineMessage({
            id: 'search-hint-body',
            message: `Body contains ${displayedValue}`,
          }),
        ),
        icon: undefined,
      };

    case ModifiersEnum.TO:
    case ModifiersEnum.FROM:
      return {
        title: displayedValue,
        icon: undefined,
      };

    case ModifiersEnum.HAS: {
      switch (value) {
        case 'attachments':
        case 'attachment':
          return {
            title: localize(
              defineMessage({
                id: 'search-hint-has-attachment',
                message: 'Has attachment',
              }),
            ),
            icon: {
              type: SearchLocalFilterTypesEnum.ICON,
              iconName: 'attachmentSmall',
              selectedIconName: 'attachmentSmall',
            },
          };

        case 'reminder':
          return {
            title: localize(
              defineMessage({
                id: 'search-hint-has-reminder',
                message: 'Has reminder',
              }),
            ),
            icon: {
              type: SearchLocalFilterTypesEnum.ICON,
              iconName: 'timeSmall',
              selectedIconName: 'timeFilledSmall',
            },
          };

        case 'no-calendar':
          return {
            title: localize(
              defineMessage({
                id: 'search-hint-has-no-calendar',
                message: 'Exclude calendar updates',
              }),
            ),
            icon: undefined,
          };

        case 'no-tags':
        case 'no-tag':
          return {
            title: localize(
              defineMessage({
                id: 'search-hint-has-no-tags',
                message: 'Has no tags',
              }),
            ),
            icon: {
              type: SearchLocalFilterTypesEnum.ICON,
              iconName: 'noTagSmall',
              selectedIconName: 'tagIsNotFilledSmall',
            },
          };

        default:
          return defaultTitleAndIcon;
      }
    }

    case ModifiersEnum.ID:
      return {
        title: localize(
          defineMessage({
            id: 'search-hint-id',
            message: `Id ${displayedValue}`,
          }),
        ),
        icon: undefined,
      };

    case ModifiersEnum.IS:
      switch (value) {
        case 'archived':
        case 'closed':
          return {
            title: localize(
              defineMessage({
                id: 'search-hint-is-archived',
                message: 'Is archived',
              }),
            ),
            icon: {
              type: SearchLocalFilterTypesEnum.ICON,
              iconName: 'archiveSmall',
              selectedIconName: 'archiveFilledSmall',
            },
          };

        case 'assigned':
          return {
            title: localize(
              defineMessage({
                id: 'search-hint-is-assigned',
                message: 'Is assigned',
              }),
            ),
            icon: {
              type: SearchLocalFilterTypesEnum.ICON,
              iconName: 'assignSmall',
              selectedIconName: 'assignFilledSmall',
            },
          };

        case 'discussion':
          return {
            title: localize(
              defineMessage({
                id: 'search-hint-is-discussion',
                message: 'Is discussion',
              }),
            ),
            icon: {
              type: SearchLocalFilterTypesEnum.ICON,
              iconName: 'discussionSmall',
              selectedIconName: 'discussionFilledSmall',
            },
          };

        case 'unsubscribed':
        case 'muted':
          return {
            title: localize(
              defineMessage({
                id: 'search-hint-is-unsubscribed',
                message: 'Is unsubscribed',
              }),
            ),
            icon: {
              type: SearchLocalFilterTypesEnum.ICON,
              iconName: 'bellCrossedSmall',
              selectedIconName: 'bellCrossedSmall',
            },
          };

        case 'open':
          return {
            title: localize(
              defineMessage({
                id: 'search-hint-is-open',
                message: 'Is open',
              }),
            ),
            icon: undefined,
          };

        case 'shared-with-me':
          return {
            title: localize(
              defineMessage({
                id: 'search-hint-is-shared-with-me',
                message: 'Is shared with me',
              }),
            ),
            icon: {
              type: SearchLocalFilterTypesEnum.ICON,
              iconName: 'teammatesSmall',
              selectedIconName: 'sharedFilledSmall',
            },
          };

        case 'snoozed':
          return {
            title: localize(
              defineMessage({
                id: 'search-hint-is-snoozed',
                message: 'Is snoozed',
              }),
            ),
            icon: {
              type: SearchLocalFilterTypesEnum.ICON,
              iconName: 'snoozeSmall',
              selectedIconName: 'snoozeFilledSmall',
            },
          };

        case 'spam':
          return {
            title: localize(
              defineMessage({
                id: 'search-hint-is-spam',
                message: 'Is spam',
              }),
            ),
            icon: {
              type: SearchLocalFilterTypesEnum.ICON,
              iconName: 'spamSmall',
              selectedIconName: 'spamFilledSmall',
            },
          };

        case 'starred':
          return {
            title: localize(
              defineMessage({
                id: 'search-hint-is-starred',
                message: 'Is starred',
              }),
            ),
            icon: {
              type: SearchLocalFilterTypesEnum.ICON,
              iconName: 'starSmall',
              selectedIconName: 'starFilledSmall',
            },
          };

        case 'subscribed':
        case 'following':
          return {
            title: localize(
              defineMessage({
                id: 'search-hint-is-subscribed',
                message: 'Is subscribed',
              }),
            ),
            icon: {
              type: SearchLocalFilterTypesEnum.ICON,
              iconName: 'subscribeSmall',
              selectedIconName: 'bellSmall',
            },
          };

        case 'trashed':
        case 'deleted':
          return {
            title: localize(
              defineMessage({
                id: 'search-hint-is-trashed',
                message: 'Is trashed',
              }),
            ),
            icon: {
              type: SearchLocalFilterTypesEnum.ICON,
              iconName: 'trashSmall',
              selectedIconName: 'trashFilledSmall',
            },
          };

        case 'unassigned':
          return {
            title: localize(
              defineMessage({
                id: 'search-hint-is-unassigned',
                message: 'Is unassigned',
              }),
            ),
            icon: {
              type: SearchLocalFilterTypesEnum.ICON,
              iconName: 'unassignedSmall',
              selectedIconName: 'unassignedFilledSmall',
            },
          };

        case 'unread':
          return {
            title: localize(
              defineMessage({
                id: 'search-hint-is-unread',
                message: 'Is unread',
              }),
            ),
            icon: {
              type: SearchLocalFilterTypesEnum.ICON,
              iconName: 'eyeCrossedSmall',
              selectedIconName: 'eyeCrossedFilledSmall',
            },
          };

        case 'unreplied':
          return {
            title: localize(
              defineMessage({
                id: 'search-hint-is-unreplied',
                message: 'Is unreplied',
              }),
            ),
            icon: undefined,
          };

        default:
          return defaultTitleAndIcon;
      }

    case ModifiersEnum.SUBJECT:
      return {
        title: localize(
          defineMessage({
            id: 'search-hint-subject',
            message: `Subject contains ${displayedValue}`,
          }),
        ),
        icon: undefined,
      };

    case ModifiersEnum.TAG:
      return {
        title: displayedValue,
        icon: {
          type: SearchLocalFilterTypesEnum.ICON,
          iconName: 'tagSmall',
          selectedIconName: 'tagFilledSmall',
        },
      };

    case ModifiersEnum.WITH:
      return {
        title: localize(
          defineMessage({
            id: 'search-hint-with',
            message: `With ${displayedValue}`,
          }),
        ),
        icon: undefined,
      };

    default:
      return defaultTitleAndIcon;
  }
}

export const maybeWrapInDoubleQuotes = (value: string) => (/\s/.test(value) ? `"${value}"` : value);

export const makeSearchFiltersVisualHint = (
  selectedSearchFilter: InboxesSearchLocationsEnum,
  localSearchFilterName: ReactNode,
  localSearchFilterIcon: SearchLocalFilterIconType,
  localize: Localizer,
  hasPreventPinTeamTags: boolean,
): VisualLocationSearchHint => {
  const values: VisualLocationSearchHint['values'] = [
    {
      value: InboxesSearchLocationsEnum.LOCAL,
      isSelected: selectedSearchFilter === InboxesSearchLocationsEnum.LOCAL,
      // @TODO: Remove cast.
      title: localSearchFilterName as string,
      icon: localSearchFilterIcon,
      key: InboxesSearchLocationsEnum.LOCAL,
      tooltip: undefined,
    },
    {
      value: InboxesSearchLocationsEnum.PINNED_IN_WORKSPACE,
      isSelected: selectedSearchFilter === InboxesSearchLocationsEnum.PINNED_IN_WORKSPACE,
      title: localize(
        defineMessage({
          id: 'search-hint-filter-sidebar',
          message: 'My sidebar',
        }),
      ),
      icon: {
        type: SearchLocalFilterTypesEnum.ICON,
        iconName: 'workspaceSmall',
        selectedIconName: 'workspaceSmall',
      },
      tooltip: hasPreventPinTeamTags
        ? localize(
            defineMessage({
              id: 'search-hint-filter-sidebar-tooltip-prevent-pin-team-tags',
              message: "Show me results from inboxes, private tags, and views that I've added to my sidebar.",
            }),
          )
        : localize(
            defineMessage({
              id: 'search-hint-filter-sidebar-tooltip',
              message:
                "Show me results from inboxes, tags, views, and teammates that I've added to my sidebar.",
            }),
          ),
      key: InboxesSearchLocationsEnum.PINNED_IN_WORKSPACE,
    },
    {
      value: InboxesSearchLocationsEnum.GLOBAL,
      isSelected: selectedSearchFilter === InboxesSearchLocationsEnum.GLOBAL,
      title: localize(
        defineMessage({
          id: 'search-hint-filter-all-inboxes',
          message: 'All inboxes',
        }),
      ),
      icon: {
        type: SearchLocalFilterTypesEnum.ICON,
        iconName: 'inboxSmall',
        selectedIconName: 'inboxFilledSmall',
      },
      tooltip: hasPreventPinTeamTags
        ? localize(
            defineMessage({
              id: 'search-hint-filter-all-inboxes-tooltip-prevent-pin-team-tags',
              message:
                'Show me search results from all inboxes, private tags, and views that I have access to.',
            }),
          )
        : localize(
            defineMessage({
              id: 'search-hint-filter-all-inboxes-tooltip',
              message:
                'Show me search results from all inboxes, tags, views, and teammates that I have access to.',
            }),
          ),
      key: InboxesSearchLocationsEnum.GLOBAL,
    },
  ];

  return {
    type: SearchStaticHintTypesEnum.SEARCH_FILTERS,
    values,
    key: SearchStaticHintTypesEnum.SEARCH_FILTERS,
  };
};
